import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useAppSelector } from '../../../../redux/hooks';
import { getTicketDetails } from '../../../../routes/support/support';
import getRandomColor from '../../../../util/style/getRandomColor';
import getStatusColor from '../../../../util/style/getStatusColor';

type Props = {
  item: any;
  onClick: () => void;
};

const SupportTicketCard: React.FC<Props> = ({ item, onClick }) => {
  const { author, _id: ticketId, status, category, createdAt } = item;
  const colors = useAppSelector(state => state.theme.colors);

  const [authorInfo, setAuthorInfo] = useState({ firstName: '', lastName: '', initials: '' });

  useEffect(() => {
    const getAuthorDetails = async () => {
      const res = await getTicketDetails(author) as any;
      if (res.data.status === '200') {
        const authorData = res.data.author as any;
        const initials = `${authorData.firstName?.[0] || ''}${authorData.lastName?.[0] || ''}`.toUpperCase();
        setAuthorInfo({
          firstName: authorData.firstName,
          lastName: authorData.lastName,
          initials: initials,
        });
      }
    };
    getAuthorDetails();
  }, [author]);

  return (
    <Box
      onClick={onClick}
      sx={{
        width: '100%',
        backgroundColor: colors?.supportEmailBackground,
        borderRadius: '8px',
        p: 2,
        mb: 2,
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
        cursor: 'pointer',
        border: `1px solid ${colors?.supportHistory}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      {/* Card Header: Author and Status */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: getRandomColor(),
              color: colors?.highlightedText,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
            }}
          >
            {authorInfo.initials}
          </Box>
          <Typography variant="subtitle1" fontWeight="bold" color={colors?.text}>
            {authorInfo.firstName} {authorInfo.lastName}
          </Typography>
        </Box>
        <Box
          sx={{
            backgroundColor: getStatusColor(status),
            color: 'white',
            borderRadius: '12px',
            px: 1.5,
            py: 0.5,
            fontSize: '12px',
            fontWeight: 'bold',
            textTransform: 'capitalize',
          }}
        >
          {status}
        </Box>
      </Box>

      {/* Card Body: Category/Subject */}
      <Typography variant="body1" color={colors?.text} sx={{ my: 1 }}>
        {category}
      </Typography>

      {/* Card Footer: Ticket ID and Date */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          ID: {ticketId}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {createdAt}
        </Typography>
      </Box>
    </Box>
  );
};

export default SupportTicketCard;